import { ManagerStoreFn, StorageManager } from '../managers/storageManager';
import { Namespace } from './types';

/**
 * Base interface for all events in the system.
 * All event types must include a timestamp.
 */
export interface BaseEvent {
  /** Timestamp when the event occurred */
  timestamp: number;
  where: string;
}

/**
 * Abstract base class for all modules in the system.
 * Provides common functionality for event handling and state management.
 * @template EventValue - The type of events this module handles
 */
export abstract class BaseModule<EventValue extends BaseEvent> {
  /** Unique namespace identifier for the module */
  abstract namespace: Namespace;

  /** Array containing the current state of events */
  public state: EventValue[] = [];

  /** Indicates if the module is ready to process events */
  public isReady = false;

  constructor() {}

  public getRequestNamespace(): string {
    return this.namespace;
  }

  /**
   * Registers a storage function for the module's namespace.
   * @param fn - Function that returns data to be stored
   */
  store(fn: ManagerStoreFn) {
    StorageManager.store(this.namespace, fn);
  }

  /**
   * Loads stored data for the module's namespace.
   * @template T - The expected type of the stored data
   * @returns The stored data cast to type T
   */
  loadStore<T>() {
    return StorageManager.load(this.namespace) as T;
  }

  //this gets called when we get a new session.
  onNewSession() {
    StorageManager.removeItem(this.namespace);
    this.purgeState();
  }

  /**
   * Sets up the module by implementing state management.
   * Limits state array to 1000 items by removing older entries.
   */
  setup() {
    this.patchStatePush();
    this.isReady = true;
  }

  private patchStatePush() {
    const org = Array.prototype.push;
    const that = this;

    this.state.push = (...args: any[]) => {
      if (that.state.length >= 1000) {
        that.state.splice(Math.floor(that.state.length / 2));
      }

      return org.apply(that.state, args);
    };
  }

  /**
   * Prepares the current state for a request.
   * Clears the current state after copying it.
   * @returns Array of events to be sent in the request
   */
  preBuildRequest() {
    const state = this.state.slice();
    this.state = [];
    this.patchStatePush();
    return state;
  }

  /**
   * Builds a request payload from the current state.
   * @param state - Array of events to include in the request
   * @returns Object containing the state and any additional data
   */
  abstract buildRequest(
    state: EventValue[],
  ): Record<string, unknown> & { state: EventValue[] };

  /**
   * Called when the module starts.
   */
  onStart() {}

  /**
   * Called when the module stops.
   */
  onStop() {}

  /**
   * Purges the module's state.
   * Must be implemented by each module.
   */
  abstract purgeState(): void;

  /**
   * Called when the module resumes from a paused state.
   * By default, calls onStart().
   */
  onResume() {
    this.onStart();
  }

  /**
   * Called when the module is paused.
   * By default, calls onStop().
   */
  onPause() {
    this.onStop();
  }
}
